import { cn } from '@/lib/utils';
import { hasTotalsBreakdown, type QuoteTotals } from '@/lib/quoteTotals';

/**
 * Subtotal → Descontos → Frete, acima da linha de Total.
 *
 * Existe como componente porque as MESMAS quatro linhas aparecem para o
 * prestador (`QuoteForm`) e para o cliente (`PublicQuotePage`), e uma divergência
 * entre as duas telas é exatamente o defeito que este grupo veio matar: o
 * prestador leria um detalhamento e o cliente, outro. O PDF renderiza o mesmo
 * conteúdo por coordenada absoluta (`renderTotalsBreakdown` em
 * `pdfTemplates/shared.ts`), com a visibilidade decidida pela mesma
 * {@link hasTotalsBreakdown}.
 *
 * Não renderiza nada quando não há o que decompor — num orçamento simples
 * "Subtotal R$ 100 / Total R$ 100" é ruído.
 */
export function QuoteTotalsBreakdown({
  totals,
  formatCurrency,
  className,
}: {
  totals: QuoteTotals;
  formatCurrency: (value: number) => string;
  className?: string;
}) {
  if (!hasTotalsBreakdown(totals)) return null;

  const rows: Array<[string, string]> = [['Subtotal', formatCurrency(totals.subtotal)]];
  if (totals.descontoItens > 0) {
    rows.push(['Descontos nos itens', `−${formatCurrency(totals.descontoItens)}`]);
  }
  if (totals.descontoCabecalho > 0) {
    rows.push(['Desconto no total', `−${formatCurrency(totals.descontoCabecalho)}`]);
  }
  if (totals.frete > 0) {
    rows.push(['Frete', `+${formatCurrency(totals.frete)}`]);
  }

  return (
    <div className={cn('space-y-1 text-sm', className)}>
      {rows.map(([label, value]) => (
        <div key={label} className="flex items-center justify-between text-muted-foreground">
          <span>{label}</span>
          <span>{value}</span>
        </div>
      ))}
    </div>
  );
}
